// mds-dojo — dojo/dom-geometry. Return {l, t, w, h} / {x, y, w, h} like dojo.
//
// Extended for the dijit/Dialog + Tooltip family port: the box-SETTER side of dojo
// dom-geometry (setMarginBox / setContentSize + the pad/border/margin extent
// helpers) is a FAITHFUL port of references/dojo/dom-geometry.js with the
// IE/quirks/webkit-button branches dropped (evergreen). boxModel is fixed to
// "content-box" (the modern default). These are purely additive — nothing here
// existed before, so no existing behavior changes.
import domStyle from './dom-style';
import dom from './dom';

export function getMarginBox(node: HTMLElement, _computedStyle?: any): { l: number; t: number; w: number; h: number } {
  // offsetWidth/Height include padding + border (the "border box"); dojo's
  // margin box adds margins. jsdom/headless returns 0s, which is fine for the
  // oracle assertion (it only checks the shape/that a measurement happened).
  const cs = (node.ownerDocument.defaultView || window).getComputedStyle(node);
  const ml = parseFloat(cs.marginLeft) || 0;
  const mt = parseFloat(cs.marginTop) || 0;
  const mr = parseFloat(cs.marginRight) || 0;
  const mb = parseFloat(cs.marginBottom) || 0;
  return {
    l: node.offsetLeft - ml,
    t: node.offsetTop - mt,
    w: node.offsetWidth + ml + mr,
    h: node.offsetHeight + mt + mb,
  };
}

export function position(node: HTMLElement, includeScroll?: boolean): { x: number; y: number; w: number; h: number } {
  const r = node.getBoundingClientRect();
  const ret = { x: r.left, y: r.top, w: r.width, h: r.height };
  // Additive (dojox/calendar): faithful dojo `includeScroll` — convert viewport
  // coords to document/page coords by adding the window scroll offset.
  if (includeScroll) {
    const win: any = (node.ownerDocument && node.ownerDocument.defaultView) || window;
    ret.x += win.pageXOffset || 0;
    ret.y += win.pageYOffset || 0;
  }
  return ret;
}

// Additive (dojox/calendar dep): faithful port of dojo dom-geometry getContentBox.
// Returns the content box {l, t, w, h} — the inner content area (padding + border
// subtracted). None existed before, so this cannot change existing behavior.
export function getContentBox(node: any, computedStyle?: any): { l: number; t: number; w: number; h: number } {
  node = dom.byId(node);
  const s = computedStyle || domStyle.getComputedStyle(node);
  let w = node.clientWidth, h: number;
  const pe = getPadExtents(node, s), be = getBorderExtents(node, s);
  if (!w) {
    w = node.offsetWidth;
    h = node.offsetHeight;
  } else {
    h = node.clientHeight;
    be.w = be.h = 0;
  }
  return { l: pe.l, t: pe.t, w: w - pe.w - be.w, h: h - pe.h - be.h };
}

export function isBodyLtr(doc?: Document): boolean {
  doc = doc || document;
  return (doc.body && doc.body.dir || doc.documentElement.dir || 'ltr').toLowerCase() === 'ltr';
}

// ---- box-model setters (faithful port of dojo dom-geometry, evergreen) -------
const boxModel: string = 'content-box';
const none = 'none';
const px = domStyle.toPixelValue;

export function getPadExtents(node: any, computedStyle?: any): any {
  node = dom.byId(node);
  const s = computedStyle || domStyle.getComputedStyle(node),
    l = px(node, s.paddingLeft), t = px(node, s.paddingTop),
    r = px(node, s.paddingRight), b = px(node, s.paddingBottom);
  return { l: l, t: t, r: r, b: b, w: l + r, h: t + b };
}

export function getBorderExtents(node: any, computedStyle?: any): any {
  node = dom.byId(node);
  const s = computedStyle || domStyle.getComputedStyle(node),
    l = s.borderLeftStyle != none ? px(node, s.borderLeftWidth) : 0,
    t = s.borderTopStyle != none ? px(node, s.borderTopWidth) : 0,
    r = s.borderRightStyle != none ? px(node, s.borderRightWidth) : 0,
    b = s.borderBottomStyle != none ? px(node, s.borderBottomWidth) : 0;
  return { l: l, t: t, r: r, b: b, w: l + r, h: t + b };
}

export function getPadBorderExtents(node: any, computedStyle?: any): any {
  node = dom.byId(node);
  const s = computedStyle || domStyle.getComputedStyle(node),
    p = getPadExtents(node, s), b = getBorderExtents(node, s);
  return { l: p.l + b.l, t: p.t + b.t, r: p.r + b.r, b: p.b + b.b, w: p.w + b.w, h: p.h + b.h };
}

export function getMarginExtents(node: any, computedStyle?: any): any {
  node = dom.byId(node);
  const s = computedStyle || domStyle.getComputedStyle(node),
    l = px(node, s.marginLeft), t = px(node, s.marginTop),
    r = px(node, s.marginRight), b = px(node, s.marginBottom);
  return { l: l, t: t, r: r, b: b, w: l + r, h: t + b };
}

function isButtonTag(node: any): boolean {
  return node.tagName.toLowerCase() == 'button' ||
    node.tagName.toLowerCase() == 'input' && (node.getAttribute('type') || '').toLowerCase() == 'button';
}

function usesBorderBox(node: any): boolean {
  return boxModel == 'border-box' || node.tagName.toLowerCase() == 'table' || isButtonTag(node);
}

function setBox(node: any, l: number, t: number, w: number, h: number, u?: string): void {
  u = u || 'px';
  const s = node.style;
  if (!isNaN(l)) { s.left = l + u; }
  if (!isNaN(t)) { s.top = t + u; }
  if (w >= 0) { s.width = w + u; }
  if (h >= 0) { s.height = h + u; }
}

export function setContentSize(node: any, box: any, computedStyle?: any): void {
  node = dom.byId(node);
  let w = box.w, h = box.h;
  if (usesBorderBox(node)) {
    const pb = getPadBorderExtents(node, computedStyle);
    if (w >= 0) { w += pb.w; }
    if (h >= 0) { h += pb.h; }
  }
  setBox(node, NaN, NaN, w, h);
}

const nilExtents = { l: 0, t: 0, w: 0, h: 0 };

export function setMarginBox(node: any, box: any, computedStyle?: any): void {
  node = dom.byId(node);
  const s = computedStyle || domStyle.getComputedStyle(node);
  let w = box.w, h = box.h;
  const pb = usesBorderBox(node) ? nilExtents : getPadBorderExtents(node, s),
    mb = getMarginExtents(node, s);
  if (w >= 0) { w = Math.max(w - pb.w - mb.w, 0); }
  if (h >= 0) { h = Math.max(h - pb.h - mb.h, 0); }
  setBox(node, box.l, box.t, w, h);
}

const domGeometry = {
  getMarginBox, getContentBox, position, isBodyLtr, boxModel,
  getPadExtents, getBorderExtents, getPadBorderExtents, getMarginExtents,
  setContentSize, setMarginBox,
};
export default domGeometry;
